import { useState, useEffect, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Shield,
  ArrowLeft,
  Building2,
  Loader2,
  Eye,
  EyeOff,
  Key,
  Fingerprint,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Turnstile } from "@/components/security/Turnstile";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { InputOTP } from "@/components/ui/inputOTP";
import { Label } from "@/components/ui/label";
import { useBotProtection } from "@/hooks/useBotProtection";
import { useTurnstile } from "@/hooks/useTurnstile";
import { authApi, ssoApi } from "../../services/api";
import { useAuthStore } from "../../store/auth.store";

// Login steps
const STEPS = {
  EMAIL: "email",
  PASSWORD: "password",
  SSO: "sso",
  TWO_FACTOR: "2fa",
};

// WebAuthn helper functions
const base64urlToBuffer = (base64url) => {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

const bufferToBase64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
};

export const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuthStore();

  const [step, setStep] = useState(STEPS.EMAIL);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [totpCode, setTotpCode] = useState("");
  const [twoFAMethod, setTwoFAMethod] = useState(""); // "email", "totp", or "webauthn"
  const [twoFAUserId, setTwoFAUserId] = useState(""); // Store userId for WebAuthn
  const [ssoConfig, setSsoConfig] = useState(null);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [webauthnLoading, setWebauthnLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  // Bot protection
  const {
    securityHeaders,
    isReady: securityReady,
    resetFormTimer,
  } = useBotProtection();
  const turnstile = useTurnstile();

  // Reset form timer when step changes
  useEffect(() => {
    resetFormTimer();
  }, [step, resetFormTimer]);

  // ============================================
  // TanStack Query - Check SSO Mutation
  // ============================================
  const checkSSOmutation = useMutation({
    mutationFn: (email) => ssoApi.checkSSO(email),
    onSuccess: (response) => {
      // Handle both response structures: response.data.data or response.data
      const data = response?.data?.data || response?.data || {};
      const { required, configId, providerType, providerName } = data;

      if (required) {
        // SSO is enforced for this domain
        setSsoConfig({ configId, providerType, providerName });
        setStep(STEPS.SSO);
      } else {
        // No SSO required, proceed to password
        setStep(STEPS.PASSWORD);
      }
    },
    onError: (error) => {
      // If SSO check fails, default to password login
      console.warn("SSO check failed, defaulting to password login:", error);
      setStep(STEPS.PASSWORD);
    },
  });

  // ============================================
  // TanStack Query - Login Mutation
  // ============================================
  const loginMutation = useMutation({
    mutationFn: (credentials) => {
      // Include turnstile token if available
      const data = { ...credentials };
      if (turnstile.token) {
        data.turnstileToken = turnstile.token;
      }
      return authApi.login(data, securityHeaders);
    },
    onSuccess: (response) => {
      const { data } = response.data;

      // Check if 2FA is required
      if (data.requires2FA) {
        setTwoFAMethod(data.method || "email"); // Store the 2FA method (email, totp, or webauthn)
        setTwoFAUserId(data.userId || ""); // Store userId for WebAuthn
        setStep(STEPS.TWO_FACTOR);
        return;
      }

      // HYBRID AUTH: accessToken returned in body, refreshToken in httpOnly cookie
      const { user, accessToken } = data;
      login(user, accessToken);

      const from = location.state?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
    },
    onError: (error) => {
      const errorData = error.response?.data?.error;

      // Handle challenge required response
      if (errorData?.code === "CHALLENGE_REQUIRED") {
        setServerError(
          t(
            "auth.login.securityCheckRequired",
            "Please complete the security verification",
          ),
        );
        turnstile.reset();
        return;
      }

      const message = errorData?.message || t("auth.login.loginError");
      setServerError(message);
      turnstile.reset();
    },
  });

  // ============================================
  // TanStack Query - 2FA Verification Mutation
  // ============================================
  const verify2FAMutation = useMutation({
    mutationFn: ({ method, email, password, totpCode, rememberMe }) => {
      if (method === "totp") {
        // TOTP (authenticator app) - call login with totpCode
        return authApi.login({
          email,
          password,
          totpCode,
          rememberMe,
        });
      }
      // Email 2FA - call verifyLoginCode endpoint
      return authApi.verifyLoginCode({
        email,
        code: totpCode, // The code entered by user (from email)
        rememberMe,
      });
    },
    onSuccess: (response) => {
      // HYBRID AUTH: accessToken returned in body, refreshToken in httpOnly cookie
      const { user, accessToken } = response.data.data;
      login(user, accessToken);

      const from = location.state?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
    },
    onError: (error) => {
      const message =
        error.response?.data?.error?.message || t("auth.twoFactor.invalidCode");
      setServerError(message);
    },
  });

  const checkingSSO = checkSSOmutation.isPending;
  const loading =
    loginMutation.isPending || verify2FAMutation.isPending || webauthnLoading;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    setServerError("");
  };

  const validateEmail = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = t("auth.validation.emailRequired");
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("auth.validation.emailInvalid");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePassword = () => {
    const newErrors = {};
    if (!formData.password) {
      newErrors.password = t("auth.validation.passwordRequired");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Step 1: Check if SSO is required for the email domain
   */
  const handleEmailSubmit = (e) => {
    e.preventDefault();
    setServerError("");

    if (!validateEmail()) return;

    checkSSOmutation.mutate(formData.email);
  };

  /**
   * Step 2b: Normal password login
   */
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setServerError("");

    if (!validatePassword()) return;

    loginMutation.mutate({
      email: formData.email,
      password: formData.password,
      rememberMe: formData.rememberMe,
    });
  };

  /**
   * Step 3: 2FA verification
   * - For email 2FA: calls verifyLoginCode endpoint
   * - For TOTP 2FA: calls login endpoint with totpCode
   */
  const handle2FASubmit = (e) => {
    e.preventDefault();
    setServerError("");

    verify2FAMutation.mutate({
      method: twoFAMethod,
      email: formData.email,
      password: formData.password,
      totpCode,
      rememberMe: formData.rememberMe,
    });
  };

  /**
   * Step 2a: Redirect to SSO provider
   */
  const handleSSOLogin = () => {
    if (!ssoConfig) return;

    const { configId, providerType } = ssoConfig;
    const relayState = encodeURIComponent(
      location.state?.from?.pathname || "/dashboard",
    );

    // Redirect to SSO login endpoint
    if (providerType === "saml") {
      window.location.href = `/api/sso/saml/${configId}/login?relayState=${relayState}`;
    } else {
      window.location.href = `/api/sso/oidc/${configId}/login?relayState=${relayState}`;
    }
  };

  /**
   * Go back to email step
   */
  const handleBack = () => {
    setStep(STEPS.EMAIL);
    setFormData((prev) => ({ ...prev, password: "" }));
    setTotpCode("");
    setTwoFAMethod(""); // Reset 2FA method
    setTwoFAUserId(""); // Reset userId for WebAuthn
    setSsoConfig(null);
    setServerError("");
    setErrors({});
    setWebauthnLoading(false);
    turnstile.reset();
  };

  /**
   * Handle WebAuthn authentication
   * Called when user clicks "Authenticate with Security Key" or automatically on mount
   */
  const handleWebAuthnAuth = useCallback(async () => {
    if (!twoFAUserId) {
      setServerError(
        t(
          "auth.twoFactor.sessionExpired",
          "Session expired. Please login again.",
        ),
      );
      return;
    }

    setWebauthnLoading(true);
    setServerError("");

    try {
      // Get WebAuthn authentication options from server
      const optionsResponse =
        await authApi.getWebAuthnLoginOptions(twoFAUserId);
      const options = optionsResponse.data?.data;

      if (!options) {
        throw new Error("Failed to get authentication options");
      }

      // Convert challenge and credential IDs from base64url to ArrayBuffer
      options.challenge = base64urlToBuffer(options.challenge);

      if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials.map((cred) => ({
          ...cred,
          id: base64urlToBuffer(cred.id),
        }));
      }

      // Prompt user to authenticate with their security key
      const credential = await navigator.credentials.get({
        publicKey: options,
      });

      if (!credential) {
        throw new Error("No credential returned");
      }

      // Prepare credential for server verification
      const credentialForServer = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
          authenticatorData: bufferToBase64url(
            credential.response.authenticatorData,
          ),
          signature: bufferToBase64url(credential.response.signature),
          userHandle: credential.response.userHandle
            ? bufferToBase64url(credential.response.userHandle)
            : undefined,
        },
        authenticatorAttachment: credential.authenticatorAttachment,
      };

      // Verify with backend
      const verifyResponse = await authApi.verifyWebAuthnLogin({
        userId: twoFAUserId,
        credential: credentialForServer,
      });

      const { user, accessToken } =
        verifyResponse.data?.data || verifyResponse.data;
      login(user, accessToken);

      const from = location.state?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
    } catch (error) {
      console.error("WebAuthn authentication error:", error);

      if (error.name === "NotAllowedError") {
        setServerError(
          t(
            "auth.twoFactor.webauthnCancelled",
            "Authentication was cancelled or timed out. Please try again.",
          ),
        );
      } else if (error.name === "NotSupportedError") {
        setServerError(
          t(
            "auth.twoFactor.webauthnNotSupported",
            "Security keys are not supported in this browser.",
          ),
        );
      } else {
        setServerError(
          error.response?.data?.error?.message ||
            error.message ||
            t(
              "auth.twoFactor.webauthnError",
              "Security key authentication failed. Please try again.",
            ),
        );
      }
    } finally {
      setWebauthnLoading(false);
    }
  }, [location.state?.from?.pathname, login, navigate, t, twoFAUserId]);

  /**
   * Handle passwordless passkey login
   * Called when user clicks "Sign in with Passkey" on the email step
   */
  const handlePasskeyLogin = async () => {
    // Check if WebAuthn is supported
    if (!window.PublicKeyCredential) {
      setServerError(
        t(
          "auth.passkey.notSupported",
          "Passkeys are not supported in this browser.",
        ),
      );
      return;
    }

    setPasskeyLoading(true);
    setServerError("");

    try {
      // Get passwordless authentication options from server
      const optionsResponse = await authApi.getPasswordlessAuthOptions();
      const options = optionsResponse.data?.data;

      if (!options) {
        throw new Error("Failed to get authentication options");
      }

      const { sessionId } = options;

      // Convert challenge from base64url to ArrayBuffer
      options.challenge = base64urlToBuffer(options.challenge);

      // Remove sessionId from options before passing to browser
      delete options.sessionId;

      // Prompt user to select and authenticate with their passkey
      // Use "optional" mediation for button-triggered flow (not "conditional" which waits for autofill)
      const credential = await navigator.credentials.get({
        publicKey: options,
      });

      if (!credential) {
        throw new Error("No credential returned");
      }

      // Prepare credential for server verification
      const credentialForServer = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
          authenticatorData: bufferToBase64url(
            credential.response.authenticatorData,
          ),
          signature: bufferToBase64url(credential.response.signature),
          userHandle: credential.response.userHandle
            ? bufferToBase64url(credential.response.userHandle)
            : undefined,
        },
        authenticatorAttachment: credential.authenticatorAttachment,
      };

      // Verify with backend
      const verifyResponse = await authApi.verifyPasswordlessLogin({
        sessionId,
        credential: credentialForServer,
      });

      const { user, accessToken } =
        verifyResponse.data?.data || verifyResponse.data;
      login(user, accessToken);

      const from = location.state?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
    } catch (error) {
      console.error("Passkey login error:", error);

      if (error.name === "NotAllowedError") {
        setServerError(
          t(
            "auth.passkey.cancelled",
            "Passkey authentication was cancelled. Please try again.",
          ),
        );
      } else if (error.name === "NotSupportedError") {
        setServerError(
          t(
            "auth.passkey.notSupported",
            "Passkeys are not supported in this browser.",
          ),
        );
      } else {
        setServerError(
          error.response?.data?.error?.message ||
            error.message ||
            t(
              "auth.passkey.error",
              "Passkey authentication failed. Please try again.",
            ),
        );
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  // Auto-trigger WebAuthn when entering 2FA step with webauthn method
  useEffect(() => {
    if (
      step === STEPS.TWO_FACTOR &&
      twoFAMethod === "webauthn" &&
      twoFAUserId
    ) {
      // Small delay to allow UI to render first
      const timer = setTimeout(() => {
        handleWebAuthnAuth();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [handleWebAuthnAuth, step, twoFAMethod, twoFAUserId]);

  // =====================================================
  // STEP: 2FA Verification
  // =====================================================
  if (step === STEPS.TWO_FACTOR) {
    // WebAuthn 2FA - different UI
    if (twoFAMethod === "webauthn") {
      return (
        <div>
          <div className="flex justify-center mb-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Key className="h-8 w-8 text-primary" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-foreground text-center mb-2">
            {t("auth.twoFactor.titleWebauthn", "Security Key Required")}
          </h2>
          <p className="text-muted-foreground text-center mb-6">
            {t(
              "auth.twoFactor.subtitleWebauthn",
              "Use your security key or passkey to complete sign in",
            )}
          </p>

          {serverError && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
              {serverError}
            </div>
          )}

          <div className="space-y-4">
            {webauthnLoading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground text-sm">
                  {t(
                    "auth.twoFactor.waitingForKey",
                    "Waiting for security key...",
                  )}
                </p>
              </div>
            ) : (
              <Button
                type="button"
                onClick={handleWebAuthnAuth}
                className="w-full"
              >
                <Key className="h-4 w-4 mr-2" />
                {t("auth.twoFactor.useSecurityKey", "Use Security Key")}
              </Button>
            )}

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={handleBack}
              disabled={webauthnLoading}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("common.back", "Back")}
            </Button>
          </div>
        </div>
      );
    }

    // Email or TOTP 2FA - existing UI
    return (
      <div>
        <div className="flex justify-center mb-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Shield className="h-8 w-8 text-primary" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-foreground text-center mb-2">
          {t("auth.twoFactor.title", "Two-Factor Authentication")}
        </h2>
        <p className="text-muted-foreground text-center mb-6">
          {twoFAMethod === "totp"
            ? t(
                "auth.twoFactor.subtitleTotp",
                "Enter the 6-digit code from your authenticator app",
              )
            : t(
                "auth.twoFactor.subtitleEmail",
                "Enter the 6-digit code sent to your email",
              )}
        </p>

        {serverError && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
            {serverError}
          </div>
        )}

        <form onSubmit={handle2FASubmit} className="space-y-4">
          <div className="space-y-2">
            {/* <Label htmlFor="totpCode">
              {t("auth.twoFactor.codeLabel", "Verification Code")}
            </Label> */}
            <InputOTP
              id="totpCode"
              name="totpCode"
              value={totpCode}
              onChange={(e) => setTotpCode(e)}
              className="text-center text-2xl tracking-widest font-mono"
              autoFocus
            />
          </div>

          <Button
            type="submit"
            disabled={loading || totpCode.length !== 6}
            className="w-full"
          >
            {loading
              ? t("auth.twoFactor.verifying", "Verifying...")
              : t("auth.twoFactor.verify", "Verify")}
          </Button>

          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={handleBack}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("common.back", "Back")}
          </Button>
        </form>
      </div>
    );
  }

  // =====================================================
  // STEP: SSO Required
  // =====================================================
  if (step === STEPS.SSO) {
    return (
      <div>
        <div className="flex justify-center mb-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Building2 className="h-8 w-8 text-primary" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-foreground text-center mb-2">
          {t("auth.sso.title", "Sign in with SSO")}
        </h2>
        <p className="text-muted-foreground text-center mb-2">
          {t("auth.sso.subtitle", "Your organisation requires single sign-on")}
        </p>
        <p className="text-sm text-muted-foreground text-center mb-6">
          {formData.email}
        </p>

        <div className="space-y-4">
          <Button type="button" onClick={handleSSOLogin} className="w-full">
            <Building2 className="h-4 w-4 mr-2" />
            {t("auth.sso.continueWith", "Continue with {{provider}}", {
              provider: ssoConfig?.providerName || "SSO",
            })}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                {t("common.or", "or")}
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={handleBack}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("auth.sso.useAnotherEmail", "Use a different email")}
          </Button>
        </div>
      </div>
    );
  }

  // =====================================================
  // STEP: Password Entry
  // =====================================================
  if (step === STEPS.PASSWORD) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-foreground text-center mb-2">
          {t("auth.login.enterPassword", "Enter your password")}
        </h2>
        <p className="text-sm text-muted-foreground text-center mb-6">
          {formData.email}
        </p>

        {serverError && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
            {serverError}
          </div>
        )}

        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password">{t("auth.login.passwordLabel")}</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder={t("auth.login.passwordPlaceholder")}
                value={formData.password}
                onChange={handleChange}
                error={errors.password}
                autoComplete="current-password"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                tabIndex={-1}
                aria-label={
                  showPassword
                    ? t("auth.login.hidePassword", "Hide password")
                    : t("auth.login.showPassword", "Show password")
                }
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password}</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="rememberMe"
                checked={formData.rememberMe}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, rememberMe: checked }))
                }
              />
              <Label
                htmlFor="rememberMe"
                className="text-sm font-normal cursor-pointer"
              >
                {t("auth.login.rememberMe")}
              </Label>
            </div>

            <Link
              to="/forgot-password"
              className="text-sm text-primary hover:text-primary/80"
            >
              {t("auth.login.forgotPassword")}
            </Link>
          </div>

          {/* Turnstile CAPTCHA - only shows if configured */}
          <Turnstile
            {...turnstile.turnstileProps}
            className="flex justify-center"
          />

          <Button
            type="submit"
            disabled={loading || !securityReady}
            className="w-full"
          >
            {loading ? t("auth.login.signingIn") : t("auth.login.signIn")}
          </Button>

          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={handleBack}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("auth.sso.useAnotherEmail", "Use a different email")}
          </Button>
        </form>
      </div>
    );
  }

  // =====================================================
  // STEP: Email Entry (Initial Step)
  // =====================================================
  return (
    <div>
      <h2 className="text-2xl font-bold text-foreground text-center mb-2">
        {t("auth.login.title")}
      </h2>
      <p className="text-muted-foreground text-center mb-6">
        {t("auth.login.subtitle")}
      </p>

      {serverError && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {serverError}
        </div>
      )}

      <form onSubmit={handleEmailSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">{t("auth.login.emailLabel")}</Label>
          <Input
            id="email"
            type="email"
            name="email"
            placeholder={t("auth.login.emailPlaceholder")}
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
            autoComplete="email"
            autoFocus
          />
          {errors.email && (
            <p className="text-sm text-destructive">{errors.email}</p>
          )}
        </div>

        <Button
          type="submit"
          disabled={checkingSSO || passkeyLoading}
          className="w-full"
        >
          {checkingSSO ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t("common.pleaseWait", "Please wait...")}
            </>
          ) : (
            t("common.continue", "Continue")
          )}
        </Button>
      </form>

      {/* Passkey Login Option */}
      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              {t("common.or", "or")}
            </span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full mt-4"
          onClick={handlePasskeyLogin}
          disabled={passkeyLoading || checkingSSO}
        >
          {passkeyLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t("auth.passkey.authenticating", "Authenticating...")}
            </>
          ) : (
            <>
              <Fingerprint className="h-4 w-4 mr-2" />
              {t("auth.passkey.signIn", "Sign in with Passkey")}
            </>
          )}
        </Button>
      </div>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        {t("auth.login.noAccount")}{" "}
        <Link
          to="/register"
          className="text-primary hover:text-primary/80 font-medium"
        >
          {t("auth.login.signUp")}
        </Link>
      </p>
    </div>
  );
};

export default Login;
